import React, { useCallback, useState } from 'react';

import { CommentImage, isHidden } from '@cobu/types';
import { Strings } from '../../../constants';
import './hiddenImageNotice.css';

function noticeText(): string {
  return Strings.get().discussion.moderation.imageHiddenTooltip;
}

/** Open/hover state for the hidden-image badges in one comment, keyed by image uuid.
 *  Closes by tapping the badge again or opening another; no outside-tap handler. */
export function useHiddenImageNotice() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const toggle = useCallback((uuid: string) => {
    setOpenId((current) => (current === uuid ? null : uuid));
    // Clear the hover the click happened inside of, else the notice stays up.
    setHoveredId((current) => (current === uuid ? null : current));
  }, []);

  /** True only while a still-hidden, still-rendered image is open or hovered.
   *  Derived, so the notice drops when Virtuoso unmounts a tile mid-hover or a
   *  refetch clears `hidden`. */
  const isActiveFor = useCallback(
    (images: CommentImage[]) =>
      images.some(
        (img) =>
          isHidden(img) && (img.uuid === openId || img.uuid === hoveredId),
      ),
    [openId, hoveredId],
  );

  return {
    openId,
    isActiveFor,
    toggle,
    setHoveredId,
  };
}

interface HiddenImageBadgeProps {
  uuid: string;
  isOpen: boolean;
  onToggle: (uuid: string) => void;
  onHoverChange: (uuid: string | null) => void;
}

/** Author-only marker on a hidden photo; reveals `HiddenImageNoticeBlock`. */
export function HiddenImageBadge({
  uuid,
  isOpen,
  onToggle,
  onHoverChange,
}: HiddenImageBadgeProps) {
  return (
    <div className="cobu-hidden-image-badge">
      <button
        type="button"
        className="cobu-hidden-image-badge-icon"
        aria-label={noticeText()}
        aria-expanded={isOpen}
        // stopPropagation keeps CobuView's onPress from opening the image viewer.
        onClick={(e) => {
          e.stopPropagation();
          onToggle(uuid);
        }}
        onMouseEnter={() => onHoverChange(uuid)}
        onMouseLeave={() => onHoverChange(null)}
      >
        <svg width={18} height={18} viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"
            fill="#e5484d"
          />
          <path
            d="M12 8.25a1 1 0 0 1 1 1v4a1 1 0 0 1-2 0v-4a1 1 0 0 1 1-1Zm0 8.25a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3Z"
            fill="#fff"
          />
        </svg>
      </button>
    </div>
  );
}

/** Renders below the images, clear of the clipping scroller. */
export function HiddenImageNoticeBlock() {
  return (
    <span className="cobu-hidden-image-notice-block" role="tooltip">
      {noticeText()}
    </span>
  );
}
